import { observer } from 'mobx-react-lite'
import React, { useContext, useState } from 'react'
import { PeopleFill, GraphUp, Image, BarChart } from 'react-bootstrap-icons'
import { CampaignContext } from '../../../../contexts/CampaignContext'
import CampaignContacts from './OverviewParts/CampaignContacts'
import OverviewKpiWidget from './OverviewParts/OverviewKpiWidget'
import OverviewHeader from './OverviewParts/OverviewHeader'
import SelectedInfluencers from './OverviewParts/SelectedInfluencers'
import Shares from './OverviewParts/Shares'
import CampaignTargetsUpdateModal from './OverviewParts/CampaignTargetsUpdateModal'
import { formatCurrencyKpi } from '../../../../utils/Helper'
import {
  budgetVisibleScenarioActions,
  isInfluencerVisibleInBudgetScenario,
} from './BudgetTableParts/influencerBudgetVisibility'

const parseNumber = (value) => {
  if (value === null || value === undefined || value === '') return 0
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0

  const compactValue = String(value).trim().replace(/\s/g, '')
  const normalizedValue = compactValue.includes(',') && compactValue.includes('.')
    ? compactValue.replace(/\./g, '').replace(',', '.')
    : compactValue.includes(',')
      ? compactValue.replace(',', '.')
      : /^\d{1,3}(\.\d{3})+$/.test(compactValue)
        ? compactValue.replace(/\./g, '')
        : compactValue
  const parsed = Number(normalizedValue)
  return Number.isFinite(parsed) ? parsed : 0
}

const sumBy = (items, selector) => (
  (items || []).reduce((result, item) => result + parseNumber(selector(item)), 0)
)

const formatCurrencyKpiValue = (value, currencySymbol) => (
  formatCurrencyKpi(Number.isFinite(value) ? value.toFixed(2) : 0, currencySymbol)
)

export default observer(function Overview() {
  const [showUpdateTargetsModal, setShowUpdateTargetsModal] = useState(false)
  const { campaign, campaignScenarios, influencers, authentications } = useContext(CampaignContext)
  const hasScenario = (campaignScenarios?.length || 0) > 0
  const activeScenarios = (campaignScenarios || []).filter(scenario => scenario.active)
  const actions = activeScenarios.flatMap(scenario =>
    budgetVisibleScenarioActions(scenario, { influencers, campaign, authentications })
  )
  const mediaActions = actions.filter(action => action.action_type !== 'non_media_item')

  const visibleInfluencersCount = hasScenario
    ? influencers.filter(inf =>
        activeScenarios.some(scenario => isInfluencerVisibleInBudgetScenario(scenario, inf, campaign, authentications))
      ).length
    : influencers.length

  const totalAdvertiserPrice = sumBy(actions, action => action.totalAdvertiserPrice)
  const currentInfluencerSpend = sumBy(actions, action => action.advertiserPrice)
  const currentBuyoutSpend = sumBy(actions, action => action.buyoutAdvertiserPrice)
  const targetTotalBudget = parseNumber(campaign.influencer_budget) + parseNumber(campaign.social_budget) + parseNumber(campaign.media_budget)

  const currentGrossReach = sumBy(mediaActions, action => action.reach)
  const currentImpressions = sumBy(mediaActions, action => action.impression)
  const targetImpressions = parseNumber(campaign.impressions_target)
  const targetGrossReach = parseNumber(campaign.gross_reach_target)

  // CPM Advertiser (Follower) — matches Budget Table Overview
  const currentCpm = currentGrossReach > 0 ? (totalAdvertiserPrice / currentGrossReach) * 1000 : 0
  const targetCpm = targetGrossReach > 0 ? (totalAdvertiserPrice / targetGrossReach) * 1000 : 0


  return (
    <>
      <CampaignTargetsUpdateModal isOpen={showUpdateTargetsModal} setIsOpen={setShowUpdateTargetsModal} />
        <OverviewHeader />
        <div className="px-4 pt-4 grid grid-cols-2 gap-4 bg-rb-grey dark:bg-rb-dark-mode-background items-stretch">
          <div className="flex flex-col min-h-0">
            <p className="font-bold mb-2">General</p>
            <div className="flex-1 flex flex-col gap-4 min-h-0">
            <div className="flex-1 grid grid-cols-3 gap-4 min-h-0 content-stretch">
              <OverviewKpiWidget
                showUpdateModal={setShowUpdateTargetsModal}
                title="Influencers"
                kpi={visibleInfluencersCount}
                target={parseNumber(campaign.influencer_target)}
                icon={<PeopleFill />}
                hasScenario={hasScenario}
              />
              <OverviewKpiWidget
                showUpdateModal={setShowUpdateTargetsModal}
                title="Number of Actions"
                kpi={mediaActions.length}
                target={parseNumber(campaign.action_count_target)}
                icon={<Image />}
                hasScenario={hasScenario}
              />
              <OverviewKpiWidget
                showUpdateModal={setShowUpdateTargetsModal}
                title="Impressions"
                kpi={currentImpressions}
                target={targetImpressions}
                icon={ <GraphUp />}
                hasScenario={hasScenario}
              />
            </div>
            <div className="flex-1 grid grid-cols-2 gap-4 min-h-0 content-stretch">
              <OverviewKpiWidget
                showUpdateModal={setShowUpdateTargetsModal}
                title="CPM"
                kpi={formatCurrencyKpiValue(currentCpm, campaign.currency_symbol)}
                target={formatCurrencyKpiValue(targetCpm, campaign.currency_symbol)}
                icon={ <BarChart />}
                hasScenario={hasScenario}
                />
              <OverviewKpiWidget
                showUpdateModal={setShowUpdateTargetsModal}
                title="Gross Reach"
                kpi={currentGrossReach}
                target={targetGrossReach}
                icon={ <BarChart />}
                hasScenario={hasScenario}
                />
            </div>
            </div>
          </div>
          <div className="flex flex-col min-h-0">
            <p className="font-bold mb-2">Budgeting</p>
            <div className="flex-1 min-h-0 grid grid-cols-1 rounded-lg border-2 border-rb-green dark:bg-rb-dark-mode-background p-2 divide-y divide-gray-300 dark:divide-gray-600">
              <div className="flex justify-between items-center py-2 p-2">
                <p className="text-sm w-1/3">Influencers</p>
                <div className="w-1/3">
                  <p className="text-sm">Current</p>
                  <p className="text-xl font-bold">{formatCurrencyKpiValue(currentInfluencerSpend, campaign.currency_symbol)}</p>
                </div>
                <div className="text-right w-1/3">
                  <p className="text-sm">Target</p>
                  <p className="text-xl font-bold">{formatCurrencyKpiValue(parseNumber(campaign.influencer_budget), campaign.currency_symbol)}</p>
                </div>
              </div>
              <div className="flex justify-between items-center py-2 p-2">
                <p className="text-sm w-1/3">Social Ads</p>
                <div className="w-1/3">
                  <p className="text-sm">Current</p>
                  <p className="text-xl font-bold">{formatCurrencyKpiValue(0, campaign.currency_symbol)}</p>
                </div>
                <div className="text-right w-1/3">
                  <p className="text-sm">Target</p>
                  <p className="text-xl font-bold">{formatCurrencyKpiValue(parseNumber(campaign.social_budget), campaign.currency_symbol)}</p>
                </div>
              </div>
              <div className="flex justify-between items-center py-2 p-2">
                <p className="text-sm w-1/3">Buyouts</p>
                <div className="w-1/3">
                  <p className="text-sm">Current</p>
                  <p className="text-xl font-bold">{formatCurrencyKpiValue(currentBuyoutSpend, campaign.currency_symbol)}</p>
                </div>
                <div className="text-right w-1/3">
                  <p className="text-sm">Target</p>
                  <p className="text-xl font-bold">{formatCurrencyKpiValue(parseNumber(campaign.media_budget), campaign.currency_symbol)}</p>
                </div>
              </div>
              <div className="flex justify-between items-center py-2 rounded-lg bg-rb-green dark:bg-rb-dark-mode-background p-2">
                <p className="text-sm w-1/3">Total</p>
                <div className="w-1/3">
                  <p className="text-sm">Current</p>
                  <p className="text-xl font-bold">{formatCurrencyKpiValue(totalAdvertiserPrice, campaign.currency_symbol)}</p>
                </div>
                <div className="text-right w-1/3">
                  <p className="text-sm">Target</p>
                  <p className="text-xl font-bold">{formatCurrencyKpiValue(targetTotalBudget, campaign.currency_symbol)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-4 bg-rb-grey dark:bg-rb-dark-mode-background">
          <SelectedInfluencers />
        </div>
        <div className="p-4 grid grid-cols-2 gap-4 mb-4 bg-rb-grey dark:bg-rb-dark-mode-background">
          <Shares />
          <CampaignContacts />
        </div>
    </>
  )
})
